/**
 * FikoGPUAngleSearch - WebGPU ile paralel açı arama
 *
 * Ozmo Kafa ve Diagonal Kafa için I,J,K vektörüne en yakın B,C açılarını
 * GPU'da paralel olarak hesaplar.
 *
 * PERFORMANS: CPU'da 360x188 = 67,680 iterasyon ~500ms
 *             GPU'da aynı hesaplama ~5ms (100x hızlanma)
 */

// WebGPU types are imported from fiko-olcum/gpu/webgpu.d.ts
// No additional type declarations needed

// WebGPU Angle Search Compute Shader
const ANGLE_SEARCH_SHADER = `
// Açı arama compute shader
// Her thread bir (B, C) açı kombinasyonu için fark hesaplar

struct SearchParams {
    // Hedef yön vektörü (I, J, K) - normalize edilmiş
    targetX: f32,
    targetY: f32,
    targetZ: f32,
    padding1: f32,

    // Arama parametreleri
    bMin: f32,      // B açısı min (genelde 0)
    bMax: f32,      // B açısı max (genelde 360)
    bStep: f32,     // B adımı
    cMin: f32,      // C açısı min (Ozmo: -94, Diagonal: 0)
    cMax: f32,      // C açısı max (Ozmo: 94, Diagonal: 360)
    cStep: f32,     // C adımı
    bCount: u32,    // B değer sayısı
    cCount: u32,    // C değer sayısı

    // Kalibrasyon değerleri (Ozmo Kafa için)
    bKalibrasyon: f32,  // B kalibrasyon (varsayılan: 0)
    cKalibrasyon: f32,  // C kalibrasyon (varsayılan: 0)
    padding3: f32,
    padding4: f32
}

struct SearchResult {
    minDiff: f32,
    bestB: f32,
    bestC: f32,
    padding: f32
}

@group(0) @binding(0) var<uniform> params: SearchParams;
@group(0) @binding(1) var<storage, read_write> results: array<SearchResult>;

const PI: f32 = 3.14159265359;
const DEG_TO_RAD: f32 = PI / 180.0;

// Axis-angle'dan rotation matrix oluştur (Rodrigues formülü)
// WGSL mat3x3f COLUMN-MAJOR: mat3x3f(col0, col1, col2)
// Three.js setRotationFromAxisAngle ile uyumlu
fn axisAngleToRotMatOzmo(angle: f32, axisX: f32, axisY: f32, axisZ: f32) -> mat3x3f {
    let c = cos(angle);
    let s = sin(angle);
    let t = 1.0 - c;

    // Ekseni normalize et (güvenlik için)
    let len = sqrt(axisX * axisX + axisY * axisY + axisZ * axisZ);
    let x = axisX / len;
    let y = axisY / len;
    let z = axisZ / len;

    // Rodrigues rotation matrix (column-major for WGSL)
    // Column 0
    let m00 = t * x * x + c;
    let m10 = t * x * y + s * z;
    let m20 = t * x * z - s * y;

    // Column 1
    let m01 = t * x * y - s * z;
    let m11 = t * y * y + c;
    let m21 = t * y * z + s * x;

    // Column 2
    let m02 = t * x * z + s * y;
    let m12 = t * y * z - s * x;
    let m22 = t * z * z + c;

    return mat3x3f(
        vec3f(m00, m10, m20),  // Column 0
        vec3f(m01, m11, m21),  // Column 1
        vec3f(m02, m12, m22)   // Column 2
    );
}

// B ve C açılarından takım yönünü hesapla (Ozmo Kafa kinematik modeli)
// ═══════════════════════════════════════════════════════════════════════════
// KRİTİK: Gerçek Ozmo kinematik modeli (UstAciOzmo.ts + AltAciOzmo.ts)
//
// Three.js Hiyerarşisi:
//   SahneOzmoKafa → cizimler → BAcisi (Üst) → CAcisi (Alt) → takim
//
// getWorldDirection() takımın +Z lokal yönünü world space'e dönüştürür.
// Rotasyonlar parent'tan child'a doğru uygulanır:
//   1. BAcisi: Z ekseni, formül: 180 - (bKalibrasyon + (360 - B)) [yon=1]
//              Basitleştirilmiş: B - 180 - bKalibrasyon
//   2. CAcisi: -X ekseni, formül: cKalibrasyon + C [yon=1]
//
// Matris çarpımında sıra: M_world = M_B * M_C * v_local
// Yani önce C rotasyonu, sonra B rotasyonu vektöre uygulanır.
// ═══════════════════════════════════════════════════════════════════════════
fn computeToolDirection(bDeg: f32, cDeg: f32) -> vec3f {
    // Takımın lokal yönü: +Z (getWorldDirection default'u)
    var dir = vec3f(0.0, 0.0, 1.0);

    // ═══════════════════════════════════════════════════════════════
    // ADIM 1: CAcisi rotasyonu (Alt açı) - önce uygulanır
    // AltAciOzmo.ts:209 - setRotationFromAxisAngle((-1, 0, 0), degToRad(cKalibrasyon + C))
    // Eksen: -X = (-1, 0, 0)
    // ═══════════════════════════════════════════════════════════════
    let cRad = (params.cKalibrasyon + cDeg) * DEG_TO_RAD;
    let rotC = axisAngleToRotMatOzmo(cRad, -1.0, 0.0, 0.0);
    dir = rotC * dir;

    // ═══════════════════════════════════════════════════════════════
    // ADIM 2: BAcisi rotasyonu (Üst açı) - sonra uygulanır
    // UstAciOzmo.ts:194 - setRotationFromAxisAngle((0,0,1), degToRad(180 - (bKalibrasyon + (360 - B))))
    // Eksen: Z = (0, 0, 1)
    // Açı: 180 - bKalibrasyon - 360 + B = B - 180 - bKalibrasyon
    // ═══════════════════════════════════════════════════════════════
    let bRad = (bDeg - 180.0 - params.bKalibrasyon) * DEG_TO_RAD;
    let rotB = axisAngleToRotMatOzmo(bRad, 0.0, 0.0, 1.0);
    dir = rotB * dir;

    return normalize(dir);
}

// İki vektör arasındaki açıyı derece cinsinden hesapla
fn angleBetween(v1: vec3f, v2: vec3f) -> f32 {
    let dotProduct = dot(normalize(v1), normalize(v2));
    // Clamp to [-1, 1] to avoid NaN from acos
    let clampedDot = clamp(dotProduct, -1.0, 1.0);
    return acos(clampedDot) * (180.0 / PI);
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    let idx = gid.x;
    let totalCombinations = params.bCount * params.cCount;

    if (idx >= totalCombinations) { return; }

    // İndeksten B ve C değerlerini hesapla
    let bIdx = idx / params.cCount;
    let cIdx = idx % params.cCount;

    let bValue = params.bMin + f32(bIdx) * params.bStep;
    let cValue = params.cMin + f32(cIdx) * params.cStep;

    // Takım yönünü hesapla
    let toolDir = computeToolDirection(bValue, cValue);

    // Hedef yön
    let targetDir = normalize(vec3f(params.targetX, params.targetY, params.targetZ));

    // Açı farkını hesapla
    let diff = angleBetween(toolDir, targetDir);

    // Sonucu yaz
    results[idx].minDiff = diff;
    results[idx].bestB = bValue;
    results[idx].bestC = cValue;
    results[idx].padding = 0.0;
}
`;

// ═══════════════════════════════════════════════════════════════════════════════
// DIAGONAL (WTEC) KAFA - WebGPU Compute Shader
// ═══════════════════════════════════════════════════════════════════════════════
// Three.js Hiyerarşisi:
//   SahneWtecKafa → cizimler → BAcisi (Üst) → CAcisi (Alt) → takim
//
// B Açısı (Üst) - bAcisiUst.ts:
//   Eksen: Z (0, 0, 1)
//   Kalibrasyon: -90° (varsayılan)
//   Formül (yon=1): kalibrasyon + B + 0.5 = B - 89.5
//
// C Açısı (Alt) - cAcisiAlt.ts:
//   Eksen: DIAGONAL! normalize(0.00617, 0.70708, -0.707107) ≈ (0, 0.707, -0.707)
//   Kalibrasyon: 0° (varsayılan)
//   Formül (yon=1): kalibrasyon + C = C
//
// Matris çarpımında sıra: M_world = M_B * M_C * v_local
// ═══════════════════════════════════════════════════════════════════════════════
const DIAGONAL_ANGLE_SEARCH_SHADER = `
// Diagonal Kafa açı arama compute shader
// Her thread bir (B, C) açı kombinasyonu için fark hesaplar

struct SearchParams {
    // Hedef yön vektörü (I, J, K) - normalize edilmiş
    targetX: f32,
    targetY: f32,
    targetZ: f32,
    padding1: f32,

    // Arama parametreleri
    bMin: f32,      // B açısı min (genelde 0)
    bMax: f32,      // B açısı max (genelde 360)
    bStep: f32,     // B adımı
    cMin: f32,      // C açısı min (Diagonal: 0)
    cMax: f32,      // C açısı max (Diagonal: 360)
    cStep: f32,     // C adımı
    bCount: u32,    // B değer sayısı
    cCount: u32,    // C değer sayısı

    // Kalibrasyon değerleri (runtime'da değiştirilebilir)
    bKalibrasyon: f32,  // B kalibrasyon (varsayılan: -90)
    cKalibrasyon: f32,  // C kalibrasyon (varsayılan: 0)
    padding3: f32,
    padding4: f32
}

struct SearchResult {
    minDiff: f32,
    bestB: f32,
    bestC: f32,
    padding: f32
}

@group(0) @binding(0) var<uniform> params: SearchParams;
@group(0) @binding(1) var<storage, read_write> results: array<SearchResult>;

const PI: f32 = 3.14159265359;
const DEG_TO_RAD: f32 = PI / 180.0;

// Axis-angle'dan rotation matrix oluştur (Rodrigues formülü)
// WGSL mat3x3f COLUMN-MAJOR: mat3x3f(col0, col1, col2)
// Three.js setRotationFromAxisAngle ile uyumlu
fn axisAngleToRotMat(angle: f32, axisX: f32, axisY: f32, axisZ: f32) -> mat3x3f {
    let c = cos(angle);
    let s = sin(angle);
    let t = 1.0 - c;

    // Ekseni normalize et (güvenlik için)
    let len = sqrt(axisX * axisX + axisY * axisY + axisZ * axisZ);
    let x = axisX / len;
    let y = axisY / len;
    let z = axisZ / len;

    // Rodrigues rotation matrix (column-major for WGSL)
    // Column 0
    let m00 = t * x * x + c;
    let m10 = t * x * y + s * z;
    let m20 = t * x * z - s * y;

    // Column 1
    let m01 = t * x * y - s * z;
    let m11 = t * y * y + c;
    let m21 = t * y * z + s * x;

    // Column 2
    let m02 = t * x * z + s * y;
    let m12 = t * y * z - s * x;
    let m22 = t * z * z + c;

    return mat3x3f(
        vec3f(m00, m10, m20),  // Column 0
        vec3f(m01, m11, m21),  // Column 1
        vec3f(m02, m12, m22)   // Column 2
    );
}

// B ve C açılarından takım yönünü hesapla (Diagonal/WTEC Kafa kinematik modeli)
fn computeToolDirectionDiagonal(bDeg: f32, cDeg: f32) -> vec3f {
    // Takımın lokal yönü: +Z (getWorldDirection default'u)
    var dir = vec3f(0.0, 0.0, 1.0);

    // ═══════════════════════════════════════════════════════════════
    // ADIM 1: CAcisi rotasyonu (Alt açı) - önce uygulanır
    // cAcisiAlt.ts:270 - setRotationFromAxisAngle(diagonal_axis, degToRad(C))
    // Eksen: (0.00617, 0.70708, -0.707107) ≈ normalize edilmiş diagonal
    // ═══════════════════════════════════════════════════════════════
    let cRad = (params.cKalibrasyon + cDeg) * DEG_TO_RAD;
    // Diagonal eksen - cAcisiAlt.ts'deki değerler
    let cAxisX: f32 = 0.00617;
    let cAxisY: f32 = 0.70708;
    let cAxisZ: f32 = -0.707107;
    let rotC = axisAngleToRotMat(cRad, cAxisX, cAxisY, cAxisZ);
    dir = rotC * dir;

    // ═══════════════════════════════════════════════════════════════
    // ADIM 2: BAcisi rotasyonu (Üst açı) - sonra uygulanır
    // bAcisiUst.ts:198 - setRotationFromAxisAngle((0,0,1), degToRad(kalibrasyon + B + 0.5))
    // Eksen: Z = (0, 0, 1)
    // Açı: kalibrasyon + B + 0.5 = -90 + B + 0.5 = B - 89.5
    // ═══════════════════════════════════════════════════════════════
    let bRad = (params.bKalibrasyon + bDeg + 0.5) * DEG_TO_RAD;
    let rotB = axisAngleToRotMat(bRad, 0.0, 0.0, 1.0);
    dir = rotB * dir;

    return normalize(dir);
}

// İki vektör arasındaki açıyı derece cinsinden hesapla
fn angleBetweenDiagonal(v1: vec3f, v2: vec3f) -> f32 {
    let dotProduct = dot(normalize(v1), normalize(v2));
    // Clamp to [-1, 1] to avoid NaN from acos
    let clampedDot = clamp(dotProduct, -1.0, 1.0);
    return acos(clampedDot) * (180.0 / PI);
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    let idx = gid.x;
    let totalCombinations = params.bCount * params.cCount;

    if (idx >= totalCombinations) { return; }

    // İndeksten B ve C değerlerini hesapla
    let bIdx = idx / params.cCount;
    let cIdx = idx % params.cCount;

    let bValue = params.bMin + f32(bIdx) * params.bStep;
    let cValue = params.cMin + f32(cIdx) * params.cStep;

    // Takım yönünü hesapla (Diagonal kinematik)
    let toolDir = computeToolDirectionDiagonal(bValue, cValue);

    // Hedef yön
    let targetDir = normalize(vec3f(params.targetX, params.targetY, params.targetZ));

    // Açı farkını hesapla
    let diff = angleBetweenDiagonal(toolDir, targetDir);

    // Sonucu yaz
    results[idx].minDiff = diff;
    results[idx].bestB = bValue;
    results[idx].bestC = cValue;
    results[idx].padding = 0.0;
}
`;

// Parallel reduction shader - minimum bulma
const REDUCTION_SHADER = `
struct SearchResult {
    minDiff: f32,
    bestB: f32,
    bestC: f32,
    padding: f32
}

@group(0) @binding(0) var<storage, read_write> results: array<SearchResult>;
@group(0) @binding(1) var<uniform> count: u32;

// NOT: 'shared' WGSL'de rezerve kelime, 'sharedData' kullanıyoruz
var<workgroup> sharedData: array<SearchResult, 256>;

@compute @workgroup_size(256)
fn main(
    @builtin(global_invocation_id) gid: vec3u,
    @builtin(local_invocation_id) lid: vec3u,
    @builtin(workgroup_id) wid: vec3u
) {
    let idx = gid.x;
    let localIdx = lid.x;

    // Load to workgroup memory
    if (idx < count) {
        sharedData[localIdx] = results[idx];
    } else {
        sharedData[localIdx].minDiff = 999999.0;
        sharedData[localIdx].bestB = 0.0;
        sharedData[localIdx].bestC = 0.0;
    }

    workgroupBarrier();

    // Parallel reduction
    for (var stride: u32 = 128u; stride > 0u; stride = stride >> 1u) {
        if (localIdx < stride && localIdx + stride < 256u) {
            if (sharedData[localIdx + stride].minDiff < sharedData[localIdx].minDiff) {
                sharedData[localIdx] = sharedData[localIdx + stride];
            }
        }
        workgroupBarrier();
    }

    // Write result from first thread of each workgroup
    if (localIdx == 0u) {
        results[wid.x] = sharedData[0];
    }
}
`;

export interface AngleSearchParams {
    targetI: number;
    targetJ: number;
    targetK: number;
    bMin?: number;
    bMax?: number;
    bStep?: number;
    cMin?: number;
    cMax?: number;
    cStep?: number;
    // Ozmo Kafa kalibrasyon değerleri
    bKalibrasyon?: number;  // B kalibrasyon (varsayılan: 0)
    cKalibrasyon?: number;  // C kalibrasyon (varsayılan: 0)
}

export interface AngleSearchResult {
    bestB: number;
    bestC: number;
    minDiff: number;
    computeTimeMs: number;
}

export interface DiagonalSearchParams extends AngleSearchParams {
    bKalibrasyon?: number;  // Varsayılan: -90
    cKalibrasyon?: number;  // Varsayılan: 0
}

export class FikoGPUAngleSearch {
    private device: GPUDevice | null = null;
    private searchPipeline: GPUComputePipeline | null = null;
    private diagonalSearchPipeline: GPUComputePipeline | null = null;  // Diagonal Kafa için
    private reductionPipeline: GPUComputePipeline | null = null;
    private isInitialized = false;
    private initPromise: Promise<boolean> | null = null;

    constructor() {
        this.initPromise = this.initialize();
    }

    private async initialize(): Promise<boolean> {
        if (this.isInitialized) return true;

        try {
            // WebGPU desteğini kontrol et
            if (!navigator.gpu) {
                return false;
            }

            const adapter = await navigator.gpu.requestAdapter({
                powerPreference: 'high-performance'
            });

            if (!adapter) {
                return false;
            }

            this.device = await adapter.requestDevice({
                requiredLimits: {
                    maxStorageBufferBindingSize: 256 * 1024 * 1024, // 256MB
                    maxBufferSize: 256 * 1024 * 1024
                }
            });

            // Search pipeline oluştur
            const searchShaderModule = this.device.createShaderModule({
                code: ANGLE_SEARCH_SHADER
            });

            this.searchPipeline = this.device.createComputePipeline({
                layout: 'auto',
                compute: {
                    module: searchShaderModule,
                    entryPoint: 'main'
                }
            });

            // Diagonal Kafa search pipeline oluştur
            const diagonalShaderModule = this.device.createShaderModule({
                code: DIAGONAL_ANGLE_SEARCH_SHADER
            });

            this.diagonalSearchPipeline = this.device.createComputePipeline({
                layout: 'auto',
                compute: {
                    module: diagonalShaderModule,
                    entryPoint: 'main'
                }
            });

            // Reduction pipeline oluştur
            const reductionShaderModule = this.device.createShaderModule({
                code: REDUCTION_SHADER
            });

            this.reductionPipeline = this.device.createComputePipeline({
                layout: 'auto',
                compute: {
                    module: reductionShaderModule,
                    entryPoint: 'main'
                }
            });

            this.isInitialized = true;
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * GPU ile açı araması yap
     * @param params Arama parametreleri
     * @returns En iyi B, C açıları ve minimum fark
     */
    async search(params: AngleSearchParams): Promise<AngleSearchResult | null> {
        const startTime = performance.now();

        // Initialization bekle
        if (this.initPromise) {
            const success = await this.initPromise;
            if (!success) {
                return null;
            }
        }

        if (!this.device || !this.searchPipeline || !this.reductionPipeline) {
            return null;
        }

        // Varsayılan değerler (Ozmo Kafa için)
        const bMin = params.bMin ?? 0;
        const bMax = params.bMax ?? 360;
        const bStep = params.bStep ?? 1;
        const cMin = params.cMin ?? -94;
        const cMax = params.cMax ?? 94;
        const cStep = params.cStep ?? 1;
        // Ozmo Kafa kalibrasyon değerleri (varsayılan: 0)
        const bKalibrasyon = params.bKalibrasyon ?? 0;
        const cKalibrasyon = params.cKalibrasyon ?? 0;

        const bCount = Math.ceil((bMax - bMin) / bStep) + 1;
        const cCount = Math.ceil((cMax - cMin) / cStep) + 1;
        const totalCombinations = bCount * cCount;

        // Hedef vektörü normalize et
        const targetLen = Math.sqrt(
            params.targetI ** 2 + params.targetJ ** 2 + params.targetK ** 2
        );
        const targetX = params.targetI / targetLen;
        const targetY = params.targetJ / targetLen;
        const targetZ = params.targetK / targetLen;

        // Uniform buffer oluştur (SearchParams - Ozmo için kalibrasyon dahil)
        const paramsData = new Float32Array([
            targetX, targetY, targetZ, 0,  // target + padding
            bMin, bMax, bStep, cMin,       // b params, cMin
            cMax, cStep,                   // cMax, cStep
            0, 0,                          // bCount, cCount as f32 placeholders
            bKalibrasyon, cKalibrasyon, 0, 0  // kalibrasyon değerleri + padding
        ]);

        // bCount ve cCount'u u32 olarak yaz
        const paramsDataView = new DataView(paramsData.buffer);
        paramsDataView.setUint32(40, bCount, true);  // offset 10 * 4 = 40
        paramsDataView.setUint32(44, cCount, true);  // offset 11 * 4 = 44

        const paramsBuffer = this.device.createBuffer({
            size: paramsData.byteLength,
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        });
        this.device.queue.writeBuffer(paramsBuffer, 0, paramsData);

        // Results buffer (her kombinasyon için 4 float)
        const resultsBufferSize = totalCombinations * 4 * 4; // 4 floats per result
        const resultsBuffer = this.device.createBuffer({
            size: resultsBufferSize,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
        });

        // Staging buffer for readback
        const stagingBuffer = this.device.createBuffer({
            size: resultsBufferSize,
            usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST
        });

        // Bind group oluştur
        const bindGroup = this.device.createBindGroup({
            layout: this.searchPipeline.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: { buffer: paramsBuffer } },
                { binding: 1, resource: { buffer: resultsBuffer } }
            ]
        });

        // Compute pass
        const commandEncoder = this.device.createCommandEncoder();
        const computePass = commandEncoder.beginComputePass();
        computePass.setPipeline(this.searchPipeline);
        computePass.setBindGroup(0, bindGroup);

        // Workgroup sayısını hesapla (256 thread per workgroup)
        const workgroupCount = Math.ceil(totalCombinations / 256);
        computePass.dispatchWorkgroups(workgroupCount);
        computePass.end();

        // Sonuçları staging buffer'a kopyala
        commandEncoder.copyBufferToBuffer(
            resultsBuffer, 0,
            stagingBuffer, 0,
            resultsBufferSize
        );

        // Submit ve bekle
        this.device.queue.submit([commandEncoder.finish()]);

        // Sonuçları oku
        await stagingBuffer.mapAsync(GPUMapMode.READ);
        const resultsArray = new Float32Array(stagingBuffer.getMappedRange().slice(0));
        stagingBuffer.unmap();

        // CPU'da minimum bul (GPU reduction yerine - daha basit)
        let minDiff = Infinity;
        let bestB = 0;
        let bestC = 0;

        for (let i = 0; i < totalCombinations; i++) {
            const diff = resultsArray[i * 4];
            if (diff < minDiff) {
                minDiff = diff;
                bestB = resultsArray[i * 4 + 1];
                bestC = resultsArray[i * 4 + 2];
            }
        }

        // Cleanup
        paramsBuffer.destroy();
        resultsBuffer.destroy();
        stagingBuffer.destroy();

        const endTime = performance.now();

        return {
            bestB,
            bestC,
            minDiff,
            computeTimeMs: endTime - startTime
        };
    }

    /**
     * İnce arama - bulunan değer etrafında daha küçük adımlarla
     */
    async fineSearch(
        params: AngleSearchParams,
        centerB: number,
        centerC: number,
        range: number = 5,
        step: number = 0.1
    ): Promise<AngleSearchResult | null> {
        return this.search({
            ...params,
            bMin: Math.max(0, centerB - range),
            bMax: Math.min(360, centerB + range),
            bStep: step,
            cMin: Math.max(params.cMin ?? -94, centerC - range),
            cMax: Math.min(params.cMax ?? 94, centerC + range),
            cStep: step
        });
    }

    /**
     * Çok aşamalı arama - fark 0'a yaklaşana kadar devam eder
     *
     * Strateji: Kademeli daraltma ile fark < epsilon olana kadar ara
     * - İlk aşama: 1° adım ile tam tarama
     * - Sonraki aşamalar: Adım 4x küçülür, aralık adımın 40 katı
     * - Fark iyileşmezse veya epsilon'a ulaşılırsa dur
     *
     * Örnek: I=0, J=1, K=0 → B=herhangi, C=90° (tam sonuç)
     */
    async multiStageSearch(params: AngleSearchParams): Promise<AngleSearchResult | null> {
        const startTime = performance.now();
        const EPSILON = 1e-6; // Float hassasiyet limiti (0.000001°)
        const MAX_STAGES = 12; // Maksimum aşama sayısı

        // ═══════════════════════════════════════════════════════════════
        // AŞAMA 1: Kaba arama (1° adım) - tam 360° tarama
        // ═══════════════════════════════════════════════════════════════
        let currentResult = await this.search({
            ...params,
            bStep: 1,
            cStep: 1
        });

        if (!currentResult) return null;

        // Fark zaten 0 veya çok küçükse dur
        if (currentResult.minDiff < EPSILON) {
            return currentResult;
        }

        // ═══════════════════════════════════════════════════════════════
        // AŞAMA 2+: Kademeli daraltma - fark 0'a yaklaşana kadar
        // ═══════════════════════════════════════════════════════════════
        let currentStep = 0.25; // Başlangıç adımı
        let stage = 2;
        let lastDiff = currentResult.minDiff;

        while (stage <= MAX_STAGES && currentStep >= 1e-7) {
            // Aralık = adımın 40 katı (her yönde 20 adım)
            const range = currentStep * 40;

            const nextResult = await this.fineSearch(
                params,
                currentResult.bestB,
                currentResult.bestC,
                range,
                currentStep
            );

            if (!nextResult) break;

            // İyileşme kontrolü
            const improvement = lastDiff - nextResult.minDiff;
            const improvementPercent = (improvement / lastDiff) * 100;

            // Fark 0'a çok yakınsa dur
            if (nextResult.minDiff < EPSILON) {
                currentResult = nextResult;
                break;
            }

            // İyileşme yoksa veya çok azsa, adımı küçült ve devam et
            if (improvement < EPSILON || improvementPercent < 0.01) {
                // Aynı noktada takılı kaldıysa adımı küçült
                currentStep /= 4;
                stage++;
                continue;
            }

            // Sonucu güncelle
            currentResult = nextResult;
            lastDiff = nextResult.minDiff;

            // Sonraki aşama için adımı küçült
            currentStep /= 4;
            stage++;
        }

        return currentResult;
    }

    /**
     * Sıfır fark araması - fark tam 0 olana kadar veya float limiti
     *
     * @param params Temel arama parametreleri
     * @param targetPrecision Hedef hassasiyet (derece), varsayılan 1e-8° (neredeyse 0)
     * @param maxStages Maksimum aşama sayısı, varsayılan 15
     *
     * Bu fonksiyon multiStageSearch'ten daha agresif arar.
     * Örnek: I=0, J=1, K=0 → C=90.00000000°, Fark=0.00000000°
     */
    async zeroSearch(
        params: AngleSearchParams,
        targetPrecision: number = 1e-8,
        maxStages: number = 15
    ): Promise<AngleSearchResult | null> {
        const startTime = performance.now();

        // İlk tam tarama - 1° adımla tam değerleri yakala (90°, 135°, 180° vb.)
        let currentResult = await this.search({
            ...params,
            bStep: 1,
            cStep: 1
        });

        if (!currentResult) return null;

        // Fark zaten 0 ise dur
        if (currentResult.minDiff < targetPrecision) {
            return this.snapToInteger(currentResult);
        }

        // Kademeli daraltma - her aşamada adım küçülür
        // Adım dizisi: 0.1, 0.01, 0.001, 0.0001, ...
        let currentStep = 0.1;
        let stage = 2;

        while (currentResult.minDiff > targetPrecision && stage <= maxStages && currentStep >= 1e-9) {
            // Aralık = adımın 20 katı (her yönde 10 adım) - daha dar aralık
            const range = Math.max(currentStep * 20, currentStep);

            const nextResult = await this.fineSearch(
                params,
                currentResult.bestB,
                currentResult.bestC,
                range,
                currentStep
            );

            if (!nextResult) break;

            // İyileşme varsa güncelle
            if (nextResult.minDiff < currentResult.minDiff) {
                currentResult = nextResult;
            }

            // Fark 0'a çok yakınsa dur
            if (currentResult.minDiff < targetPrecision) {
                break;
            }

            // Sonraki aşama için adımı 10x küçült (daha hızlı yakınsama)
            currentStep /= 10;
            stage++;
        }

        // Sonucu tam sayıya snap et (0.01° toleransla)
        currentResult = this.snapToInteger(currentResult);

        return currentResult;
    }

    /**
     * Sonucu tam sayıya snap et (eğer çok yakınsa)
     * 134.99° → 135°, 89.99° → 90° gibi
     */
    private snapToInteger(result: AngleSearchResult): AngleSearchResult {
        const SNAP_THRESHOLD = 0.02; // 0.02° = 1.2 arcmin tolerans

        let bestB = result.bestB;
        let bestC = result.bestC;

        // B açısını tam sayıya snap et
        const roundedB = Math.round(bestB);
        if (Math.abs(bestB - roundedB) < SNAP_THRESHOLD) {
            bestB = roundedB;
        }

        // C açısını tam sayıya snap et
        const roundedC = Math.round(bestC);
        if (Math.abs(bestC - roundedC) < SNAP_THRESHOLD) {
            bestC = roundedC;
        }

        return {
            ...result,
            bestB,
            bestC
        };
    }

    /**
     * WebGPU kullanılabilir mi?
     */
    async isAvailable(): Promise<boolean> {
        if (this.initPromise) {
            return await this.initPromise;
        }
        return this.isInitialized;
    }

    /**
     * Kaynakları temizle
     */
    dispose(): void {
        this.device = null;
        this.searchPipeline = null;
        this.diagonalSearchPipeline = null;
        this.reductionPipeline = null;
        this.isInitialized = false;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // DIAGONAL (WTEC) KAFA ARAMA FONKSİYONLARI
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Diagonal Kafa için GPU ile açı araması yap
     * @param params Arama parametreleri (kalibrasyon dahil)
     * @returns En iyi B, C açıları ve minimum fark
     */
    async searchDiagonal(params: DiagonalSearchParams): Promise<AngleSearchResult | null> {
        const startTime = performance.now();

        // Initialization bekle
        if (this.initPromise) {
            const success = await this.initPromise;
            if (!success) {
                return null;
            }
        }

        if (!this.device || !this.diagonalSearchPipeline || !this.reductionPipeline) {
            return null;
        }

        // Varsayılan değerler (Diagonal Kafa için)
        const bMin = params.bMin ?? 0;
        const bMax = params.bMax ?? 360;
        const bStep = params.bStep ?? 1;
        const cMin = params.cMin ?? 0;
        const cMax = params.cMax ?? 360;
        const cStep = params.cStep ?? 1;
        const bKalibrasyon = params.bKalibrasyon ?? -90;
        const cKalibrasyon = params.cKalibrasyon ?? 0;

        const bCount = Math.ceil((bMax - bMin) / bStep) + 1;
        const cCount = Math.ceil((cMax - cMin) / cStep) + 1;
        const totalCombinations = bCount * cCount;

        // Hedef vektörü normalize et
        const targetLen = Math.sqrt(
            params.targetI ** 2 + params.targetJ ** 2 + params.targetK ** 2
        );
        const targetX = params.targetI / targetLen;
        const targetY = params.targetJ / targetLen;
        const targetZ = params.targetK / targetLen;

        // Uniform buffer oluştur (SearchParams - Diagonal için kalibrasyon dahil)
        const paramsData = new Float32Array([
            targetX, targetY, targetZ, 0,  // target + padding
            bMin, bMax, bStep, cMin,       // b params, cMin
            cMax, cStep,                   // cMax, cStep
            0, 0,                          // bCount, cCount as f32 placeholders
            bKalibrasyon, cKalibrasyon, 0, 0  // kalibrasyon değerleri + padding
        ]);

        // bCount ve cCount'u u32 olarak yaz
        const paramsDataView = new DataView(paramsData.buffer);
        paramsDataView.setUint32(40, bCount, true);  // offset 10 * 4 = 40
        paramsDataView.setUint32(44, cCount, true);  // offset 11 * 4 = 44

        const paramsBuffer = this.device.createBuffer({
            size: paramsData.byteLength,
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        });
        this.device.queue.writeBuffer(paramsBuffer, 0, paramsData);

        // Results buffer (her kombinasyon için 4 float)
        const resultsBufferSize = totalCombinations * 4 * 4;
        const resultsBuffer = this.device.createBuffer({
            size: resultsBufferSize,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
        });

        // Staging buffer for readback
        const stagingBuffer = this.device.createBuffer({
            size: resultsBufferSize,
            usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST
        });

        // Bind group oluştur (Diagonal pipeline için)
        const bindGroup = this.device.createBindGroup({
            layout: this.diagonalSearchPipeline.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: { buffer: paramsBuffer } },
                { binding: 1, resource: { buffer: resultsBuffer } }
            ]
        });

        // Compute pass
        const commandEncoder = this.device.createCommandEncoder();
        const computePass = commandEncoder.beginComputePass();
        computePass.setPipeline(this.diagonalSearchPipeline);
        computePass.setBindGroup(0, bindGroup);

        // Workgroup sayısını hesapla (256 thread per workgroup)
        const workgroupCount = Math.ceil(totalCombinations / 256);
        computePass.dispatchWorkgroups(workgroupCount);
        computePass.end();

        // Sonuçları staging buffer'a kopyala
        commandEncoder.copyBufferToBuffer(
            resultsBuffer, 0,
            stagingBuffer, 0,
            resultsBufferSize
        );

        // Submit ve bekle
        this.device.queue.submit([commandEncoder.finish()]);

        // Sonuçları oku
        await stagingBuffer.mapAsync(GPUMapMode.READ);
        const resultsArray = new Float32Array(stagingBuffer.getMappedRange().slice(0));
        stagingBuffer.unmap();

        // CPU'da minimum bul
        let minDiff = Infinity;
        let bestB = 0;
        let bestC = 0;

        for (let i = 0; i < totalCombinations; i++) {
            const diff = resultsArray[i * 4];
            if (diff < minDiff) {
                minDiff = diff;
                bestB = resultsArray[i * 4 + 1];
                bestC = resultsArray[i * 4 + 2];
            }
        }

        // Cleanup
        paramsBuffer.destroy();
        resultsBuffer.destroy();
        stagingBuffer.destroy();

        const endTime = performance.now();

        return {
            bestB,
            bestC,
            minDiff,
            computeTimeMs: endTime - startTime
        };
    }

    /**
     * Diagonal Kafa için ince arama
     */
    async fineSearchDiagonal(
        params: DiagonalSearchParams,
        centerB: number,
        centerC: number,
        range: number = 5,
        step: number = 0.1
    ): Promise<AngleSearchResult | null> {
        return this.searchDiagonal({
            ...params,
            bMin: Math.max(0, centerB - range),
            bMax: Math.min(360, centerB + range),
            bStep: step,
            cMin: Math.max(params.cMin ?? 0, centerC - range),
            cMax: Math.min(params.cMax ?? 360, centerC + range),
            cStep: step
        });
    }

    /**
     * Diagonal Kafa için sıfır fark araması
     * Fark tam 0 olana kadar veya float limiti
     */
    async zeroSearchDiagonal(
        params: DiagonalSearchParams,
        targetPrecision: number = 1e-8,
        maxStages: number = 15
    ): Promise<AngleSearchResult | null> {
        const startTime = performance.now();

        // İlk tam tarama - 1° adımla tam değerleri yakala
        let currentResult = await this.searchDiagonal({
            ...params,
            bStep: 1,
            cStep: 1
        });

        if (!currentResult) return null;

        // Fark zaten 0 ise dur
        if (currentResult.minDiff < targetPrecision) {
            return this.snapToInteger(currentResult);
        }

        // Kademeli daraltma
        let currentStep = 0.1;
        let stage = 2;

        while (currentResult.minDiff > targetPrecision && stage <= maxStages && currentStep >= 1e-9) {
            const range = Math.max(currentStep * 20, currentStep);

            const nextResult = await this.fineSearchDiagonal(
                params,
                currentResult.bestB,
                currentResult.bestC,
                range,
                currentStep
            );

            if (!nextResult) break;

            if (nextResult.minDiff < currentResult.minDiff) {
                currentResult = nextResult;
            }

            if (currentResult.minDiff < targetPrecision) {
                break;
            }

            currentStep /= 10;
            stage++;
        }

        // Sonucu tam sayıya snap et
        currentResult = this.snapToInteger(currentResult);

        return currentResult;
    }
}

// Singleton instance
let gpuAngleSearchInstance: FikoGPUAngleSearch | null = null;

export function getGPUAngleSearch(): FikoGPUAngleSearch {
    if (!gpuAngleSearchInstance) {
        gpuAngleSearchInstance = new FikoGPUAngleSearch();
    }
    return gpuAngleSearchInstance;
}
